const HowItWorks = () => {
  const steps = [
    {
      number: 1,
      title: 'Scraped Data',
      description: 'Financial information taken from "Централен Регистар на Македонија" and processed by CompanyWall.mk',
      icon: (
        <div className="relative w-48 h-32 flex items-center justify-center">
          {/* Document with data icon */}
          <div className="bg-white rounded-xl shadow-lg p-4 w-36 relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-primary-green/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-primary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="h-2 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-2 bg-gray-100 rounded w-full"></div>
              <div className="h-2 bg-gray-100 rounded w-3/4"></div>
              <div className="h-2 bg-gray-100 rounded w-5/6"></div>
            </div>
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-green rounded-full flex items-center justify-center shadow-md">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          </div>
        </div>
      ),
    },
    {
      number: 2,
      title: 'Financial Exploration',
      description: 'Your financials run through a LSTM neural network with financials from all companies in Macedonia to understand the latest trends, creating a weighted prediction for the next year',
      icon: (
        <div className="relative w-48 h-32 flex items-center justify-center">
          {/* Bar chart icon */}
          <div className="flex items-end gap-2 h-24">
            <div className="w-6 bg-primary-green/30 rounded-t h-8"></div>
            <div className="w-6 bg-primary-green/50 rounded-t h-14"></div>
            <div className="w-6 bg-primary-green/70 rounded-t h-20"></div>
            <div className="w-6 bg-primary-green rounded-t h-16"></div>
            <div className="w-6 bg-primary-green/80 rounded-t h-24"></div>
          </div>
          <div className="absolute top-0 right-4 w-8 h-8 bg-primary-green rounded-full flex items-center justify-center shadow-md">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        </div>
      ),
    },
    {
      number: 3,
      title: 'Credit Rating',
      description: 'Your credit rating from the previous years is also classified and taken into consideration',
      icon: (
        <div className="relative w-48 h-32 flex items-center justify-center">
          {/* Rating card icon */}
          <div className="bg-primary-green rounded-xl shadow-lg p-4 w-32 text-white">
            <div className="flex items-center justify-between mb-2">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
              <span className="text-xs opacity-70">Rating</span>
            </div>
            <div className="text-2xl font-bold">A+</div>
            <div className="flex items-center gap-1 mt-2">
              <span className="text-xs opacity-70">Score:</span>
              <span className="text-sm font-semibold">850</span>
            </div>
          </div>
          <div className="absolute -left-4 top-1/2 -translate-y-1/2 bg-white rounded-lg shadow-md p-2 text-xs text-gray-600">
            + History
          </div>
        </div>
      ),
    },
    {
      number: 4,
      title: 'Prediction',
      description: 'Based on all factors a prediction is generated for credit eligibility',
      icon: (
        <div className="relative w-48 h-32 flex items-center justify-center">
          {/* Prediction result icon */}
          <div className="bg-white rounded-xl shadow-lg p-4 w-36 border-2 border-primary-green">
            <div className="flex items-center justify-center mb-2">
              <div className="w-12 h-12 bg-primary-green/10 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-primary-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <div className="text-primary-green font-bold text-lg">Eligible</div>
              <div className="text-xs text-gray-500">Credit Ready</div>
            </div>
          </div>
          <div className="absolute -top-2 right-4 w-8 h-8 bg-primary-green rounded-full flex items-center justify-center shadow-md animate-pulse">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section id="how-it-works" className="min-h-screen py-20 bg-gradient-to-b from-gray-50 to-white flex items-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">How it works</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our advanced AI-powered system analyzes multiple data sources to provide accurate credit eligibility predictions for businesses in Macedonia
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line - desktop */}
          <div className="hidden lg:block absolute h-0.5 border-t-2 border-dashed border-gray-300" style={{ top: '80px', left: '-40px', right: '-40px' }}></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <div key={step.number} className="relative flex flex-col items-center text-center">
                {/* Icon */}
                <div className="mb-6 relative z-10 bg-gradient-to-b from-gray-50 to-white">
                  {step.icon}
                </div>

                {/* Arrow between steps - desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-20 -right-2 z-20">
                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                    </svg>
                  </div>
                )}

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-xs">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
